import {
    Article4LongDescription,
    ConservationAreasLongDescription,
    HeritageLongDescription,
    NeighbourhoodAreasDescription,
    PlacesLongDescription,
} from '../copy/layerInfo';

const GIS2_BASE_URL = 'https://gis.london.gov.uk/arcgis/rest/services/apps/';
const GEOJSON_BASE_QUERY = '/query/?where=1%3D1&outfields=*&f=geojson';
const DEFAULT_FILL_OPACITY = 0.4;

export const groupingLabels = {
    heritage: 'Statutory and Local Heritage Listings',
    flood: 'Flood Risk',
    hs2: 'HS2 Safeguarding August 2016',
    'nature-conservation': 'Sites of Importance for Nature Conservation',
    waste: 'Waste Sites',
    'article-4': 'Article 4 Directions',
    'neighbourhood-areas': 'Neighbourhood Areas',
};

export const groupingInfo = {
    'nature-conservation': {
        shortDescription:
            'Designated areas which are recognised as being of particular importance to wildlife and biodiversity.',
    },
    flood: { shortDescription: 'Areas of river and surface water flooding' },
    heritage: {
        longDescription: HeritageLongDescription,
        longDescriptionTitle: 'Heritage Layers Information',
    },
    waste: { shortDescription: '11 waste management sites' },
    'neighbourhood-areas': {
        longDescriptionTitle: 'Neighbourhood Areas Layers Information',
        longDescription: NeighbourhoodAreasDescription,
    },
};

export const layerDefs = {
    places: {
        layer: {
            id: 'places',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(255,255,0)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
                'fill-antialias': true,
                'fill-outline-color': 'rgba(0,0,0,1)',
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'places',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/1${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: true,
            filterPropertyName: 'site_name',
            filterState: {
                'Channel Gate': false,
                'Grand Union Canal': false,
                'North Acton and Acton Wells': false,
                'Old Oak Lane and Old Oak Common Lane': false,
                'Old Oak North': false,
                'Old Oak Park Royal': false,
                'Old Oak South': false,
                'Park Royal Centre': false,
                'Park Royal West': false,
                'Scrubs Lane': false,
                'Willesden Junction': false,
                'Wormwood Scrubs': false,
            },
        },
        colours: {
            colourState: {
                'Channel Gate': 'rgb(255,255,0)',
                'Grand Union Canal': 'rgb(255,255,0)',
                'North Acton and Acton Wells': 'rgb(255,255,0)',
                'Old Oak Lane and Old Oak Common Lane': 'rgb(255,255,0)',
                'Old Oak North': 'rgb(255,255,0)',
                'Old Oak Park Royal': 'rgb(255,255,0)',
                'Old Oak South': 'rgb(255,255,0)',
                'Park Royal Centre': 'rgb(255,255,0)',
                'Park Royal West': 'rgb(255,255,0)',
                'Scrubs Lane': 'rgb(255,255,0)',
                'Willesden Junction': 'rgb(255,255,0)',
                'Wormwood Scrubs': 'rgb(255,255,0)',
            },
        },
        labels: {
            parent: 'Places',
        },
        category: 'planning',
        popupAttributes: ['site_reference', 'site_name'],
        info: {
            longDescription: PlacesLongDescription,
            longDescriptionTitle: 'Places Layer Information',
        },
    },
    'places-boundary': {
        layer: {
            id: 'places-boundary',
            type: 'line',
            paint: {
                'line-color': '#000',
                'line-width': 2,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'places-boundary',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/1${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: true,
            filterPropertyName: 'site_name',
            filterState: {
                'Channel Gate': false,
                'Grand Union Canal': false,
                'North Acton and Acton Wells': false,
                'Old Oak Lane and Old Oak Common Lane': false,
                'Old Oak North': false,
                'Old Oak Park Royal': false,
                'Old Oak South': false,
                'Park Royal Centre': false,
                'Park Royal West': false,
                'Scrubs Lane': false,
                'Willesden Junction': false,
                'Wormwood Scrubs': false,
            },
        },
        colours: {
            colourState: {
                'Channel Gate': '#000',
                'Grand Union Canal': '#000',
                'North Acton and Acton Wells': '#000',
                'Old Oak Lane and Old Oak Common Lane': '#000',
                'Old Oak North': '#000',
                'Old Oak Park Royal': '#000',
                'Old Oak South': '#000',
                'Park Royal Centre': '#000',
                'Park Royal West': '#000',
                'Scrubs Lane': '#000',
                'Willesden Junction': '#000',
                'Wormwood Scrubs': '#000',
            },
        },
        labels: {
            parent: 'Places',
        },
        category: 'planning',
        popupAttributes: ['site_reference', 'site_name'],
        info: {
            longDescription: PlacesLongDescription,
            longDescriptionTitle: 'Places Layer Information',
        },
    },
    'town-centres-neighbourhood': {
        layer: {
            id: 'town-centres-neighbourhood',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(194,158,215)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'town-centres-neighbourhood',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/4${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Neighbourhood Town Centres',
        },
        category: 'planning',
        popupAttributes: ['designation', 'site_name'],
        info: {
            longDescriptionTitle:
                'Town Centres - Neighbourhood Layer Information',
            longDescription:
                'The Local Plan identifies the Old Oak Major Town Centre, the North Acton Neighbourhood Town Centre and the Park Royal Neighbourhood Town Centre. The boundary of the Old Oak Major Town Centre has yet to be defined. Therefore it is not shown on the Policies Map but can be seen on figure 3.7 in the Local Plan.',
        },
    },
    'article-4': {
        layer: {
            id: 'article-4',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(104,104,104)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'article-4',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/5${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Article 4 Direction 2017 (Storage and distribution uses to residential)',
        },
        category: 'planning',
        popupAttributes: ['designation'],
        grouping: 'article-4',
        info: {
            longDescriptionTitle:
                'Article 4 Direction 2017 (Storage and distribution uses to residential) Layer Information',
            longDescription: Article4LongDescription,
        },
    },
    'article-4-2022': {
        layer: {
            id: 'article-4-2022',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(104,104,104)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'article-4-2022',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/20${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Article 4 Direction 2022 (Commercial, Business and Service to residential)',
        },
        category: 'planning',
        popupAttributes: ['designation'],
        grouping: 'article-4',
        info: {
            longDescriptionTitle:
                'Article 4 Direction 2022 (Commercial, Business and Service to residential) Layer Information',
            longDescription:
                'The 2022 Article 4 Direction came into effect on 22 June 2022. This removes permitted development rights for changes of use from Commercial, Business and Service (use class E) to residential (use class C3) within the Strategic Industrial Location (SIL).',
        },
    },
    'article-4-2023': {
        layer: {
            id: 'article-4-2023',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(104,104,104)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'article-4-2023',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/35${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Article 4 Direction 2023 (C3 dwelling house to C4 house in multiple occupation)',
        },
        category: 'planning',
        popupAttributes: ['designation'],
        grouping: 'article-4',
        info: {
            longDescriptionTitle:
                'Article 4 Direction 2023 (C3 dwelling house to C4 house in multiple occupation) Layer Information',
            longDescription:
                'The 2023 Article 4 Direction came into effect on 23 November 2023. This removes permitted development rights to change the use of C3 dwelling house to C4 house in multiple occupation in the residential areas of Old Oak Lane Conservation Area, Wells House Road, Shaftesbury Avenue, Midland Terrace, Wesley Estate and Western Avenue.',
        },
    },
    'old-oak-neighbourhood-area': {
        layer: {
            id: 'old-oak-neighbourhood-area',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(255,0,0)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'old-oak-neighbourhood-area',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/31${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Old Oak Neighbourhood Area',
        },
        category: 'planning',
        popupAttributes: ['site_name'],
        grouping: 'neighbourhood-areas',
    },
    'harlesden-inside-boundary': {
        layer: {
            id: 'harlesden-inside-boundary',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(0,92,230)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'harlesden-inside-boundary',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/32${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Harlesden Neighbourhood Area (Inside OPDC Boundary)',
        },
        category: 'planning',
        popupAttributes: ['site_name'],
        grouping: 'neighbourhood-areas',
    },
    'harlesden-outside-boundary': {
        layer: {
            id: 'harlesden-outside-boundary',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(0,150,300)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'harlesden-outside-boundary',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/33${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Harlesden Neighbourhood Area (Outside OPDC Boundary)',
        },
        category: 'planning',
        popupAttributes: ['site_name'],
        grouping: 'neighbourhood-areas',
    },
    'conservation-areas': {
        layer: {
            id: 'conservation-areas',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(230,0,169)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'conservation-areas',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/6${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Conservation Areas',
        },
        category: 'planning',
        popupAttributes: ['designation', 'site_name'],
        info: {
            longDescription: ConservationAreasLongDescription,
            longDescriptionTitle: 'Conservation Areas Layer Information',
        },
    },
    'heritage-listings-statutory': {
        layer: {
            id: 'heritage-listings-statutory',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(168,0,132)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'heritage-listings-statutory',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/8${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Heritage Listings - Statutory Listed Asset (designated heritage asset)',
        },
        category: 'planning',
        grouping: 'heritage',
        popupAttributes: ['designation', 'classification'],
    },
    'heritage-listings-local': {
        layer: {
            id: 'heritage-listings-local',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(245,122,182)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'heritage-listings-local',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/9${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Heritage Listings - Local Heritage Listing (non-designated heritage asset)',
        },
        category: 'planning',
        grouping: 'heritage',
        popupAttributes: ['designation', 'classification'],
    },
    'flood-risk-greater-0.1': {
        layer: {
            id: 'flood-risk-greater-0.1',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(0,197,255)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'flood-risk-greater-0.1',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/11${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'in 200 Year Flood Risk > 0.1m',
        },
        grouping: 'flood',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'flood-risk-greater-0.3': {
        layer: {
            id: 'flood-risk-greater-0.3',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(0,92,230)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'flood-risk-greater-0.3',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/12${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'in 200 Year Flood Risk > 0.3m',
        },
        grouping: 'flood',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'national-floodzone-3': {
        layer: {
            id: 'national-floodzone-3',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(0,255,197)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'national-floodzone-3',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/13${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'National Floodzone 3',
        },
        grouping: 'flood',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'national-floodzone-2': {
        layer: {
            id: 'national-floodzone-2',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(0,168,132)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'national-floodzone-2',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/14${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'National Floodzone 2',
        },
        grouping: 'flood',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'hs2-protection': {
        layer: {
            id: 'hs2-protection',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(104,104,104)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'hs2-protection',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/16${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'HS2 Extended Homeowner Protection Zones (August 2016)',
        },
        grouping: 'hs2',
        category: 'planning',
        popupAttributes: ['designation'],
        info: {
            shortDescription:
                'High Speed 2 Extended Homeowner Protection Zones',
        },
    },
    'hs2-safeguarding': {
        layer: {
            id: 'hs2-safeguarding',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(168,112,0)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'hs2-safeguarding',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/17${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'HS2 Safeguarding (August 2016)',
        },
        grouping: 'hs2',
        category: 'planning',
        popupAttributes: ['designation'],
        info: {
            shortDescription: 'High Speed 2 safeguarding areas',
        },
    },
    'strategic-industrial-locations': {
        layer: {
            id: 'strategic-industrial-locations',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(230,152,0)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'strategic-industrial-locations',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/18${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Strategic Industrial Locations',
        },
        category: 'planning',
        popupAttributes: ['designation'],
        info: {
            longDescriptionTitle: 'Strategic Industrial Locations',
            longDescription:
                'Locations protected for uses suitable for broad industrial type activities to ensure that London provides sufficient quality sites, in appropriate locations, to meet the needs of industrial and related sectors including general and light Industrial uses, logistics, waste management and environmental industries (such as renewable energy generation), utilities, wholesale markets and some transport functions.',
        },
    },
    'local-importance': {
        layer: {
            id: 'local-importance',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(233, 255, 190)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'local-importance',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/30${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Local Importance',
        },
        grouping: 'nature-conservation',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'borough-importance-2': {
        layer: {
            id: 'borough-importance-2',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(199,215,158)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'borough-importance-2',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/21${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Borough Importance 2',
        },
        grouping: 'nature-conservation',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'borough-importance-1': {
        layer: {
            id: 'borough-importance-1',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(137,205,102)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'borough-importance-1',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/22${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Borough Importance 1',
        },
        grouping: 'nature-conservation',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'metropolitan-importance': {
        layer: {
            id: 'metropolitan-importance',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(56,168,0)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'metropolitan-importance',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/23${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Metropolitan Importance',
        },
        grouping: 'nature-conservation',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },

    'metropolitan-open-land': {
        layer: {
            id: 'metropolitan-open-land',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(56,168,0)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'metropolitan-open-land',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/24${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Metropolitan Open Land',
        },
        category: 'planning',
        popupAttributes: ['designation'],
        info: {
            longDescriptionTitle: 'Metropolitan Open Land Layer Information',
            longDescription:
                'Strategic open land within the urban area that contributes to the structure of London with the same planning status as Green Belt.',
        },
    },
    'site-allocations': {
        layer: {
            id: 'site-allocations',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(255,0,197)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'site-allocations',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/25${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Site Allocations',
        },
        category: 'planning',
        popupAttributes: ['designation', 'site_reference'],
        info: {
            shortDescription:
                '42 sites used to demonstrate our supply of land for new housing, commercial and industrial floorspace.',
        },
    },
    'waste-site-H&F': {
        layer: {
            id: 'waste-site-H&F',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(230,230,0)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'waste-site-H&F',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/27${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Waste site (London Borough of Hammersmith and Fulham 2018)',
        },
        grouping: 'waste',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'waste-site-WL-waste-plan': {
        layer: {
            id: 'waste-site-WL-waste-plan',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(205,102,102)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'waste-site-WL-waste-plan',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/28${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Waste site (West London Waste Plan 2015)',
        },
        grouping: 'waste',
        category: 'planning',
        popupAttributes: ['designation', 'classification'],
    },
    'safeguarded-gypsy-traveller-site': {
        layer: {
            id: 'safeguarded-gypsy-traveller-site',
            type: 'fill',
            paint: {
                'fill-color': 'rgb(107,126,147)',
                'fill-opacity': DEFAULT_FILL_OPACITY,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'safeguarded-gypsy-traveller-site',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/29${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Safeguarded Gypsy and Traveller Site',
        },
        category: 'planning',
        popupAttributes: ['designation'],
        info: {
            shortDescription:
                'Safeguarded Gypsy and Traveller Site on Bashley Road.',
        },
    },
    'opdc-boundary': {
        layer: {
            id: 'opdc-boundary',
            type: 'line',
            paint: {
                'line-color': 'rgb(0,0,0)',
                'line-width': 2,
                'line-opacity': 1,
            },
            layout: {
                visibility: 'visible',
            },
        },
        source: {
            id: 'opdc-boundary',
            type: 'geojson',
            data: `${GIS2_BASE_URL}webmap_context_layer/MapServer/6${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'OPDC Boundary',
        },
        category: 'context',
    },
    'borough-boundary': {
        layer: {
            id: 'borough-boundary',
            type: 'line',
            paint: {
                'line-color': 'rgb(0,0,0)',
                'line-width': 2,
                'line-opacity': 1,
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'borough-boundary',
            type: 'geojson',
            data: `${GIS2_BASE_URL}webmap_context_layer/MapServer/3${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'London Borough',
        },
        category: 'context',
    },
    'existing-enhanced-routes': {
        layer: {
            id: 'existing-enhanced-routes',
            type: 'line',
            paint: {
                'line-color': 'rgb(130,130,130)',
                'line-opacity': 1,
                'line-width': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    5,
                    0.35,
                    10,
                    1.5,
                    12,
                    1.7,
                    14,
                    3.5,
                    15,
                    6.5,
                    16,
                    15,
                    18,
                    44,
                    22,
                    120,
                ],
            },
            layout: {
                visibility: 'none',
            },
        },
        source: {
            id: 'existing-enhanced-routes',
            type: 'geojson',
            data: `${GIS2_BASE_URL}OPDC_public_policies_map/MapServer/0${GEOJSON_BASE_QUERY}`,
        },
        filters: {
            filterByChildren: false,
        },
        labels: {
            parent: 'Existing Enhanced Routes',
        },
        category: 'planning',
        popupAttributes: ['designation'],
        info: {
            shortDescription: 'Routes proposed for enhancement',
        },
    },
};
